/**
 * useFields Composable
 *
 * The fields of a record type: its fixed columns, its type schema, the other
 * schemas of the type, and the relation fields. Sourced from the preloaded
 * types and schemas stores — reactive, no fetch.
 *
 * `schemaIds` narrows the other schemas to those a loaded set of records uses;
 * null (nothing loaded yet) keeps every schema of the type, each under its plain
 * key. Among the narrowed schemas, a key several declare is one field when they
 * define it alike, and one field per definition, prefixed with its schema's
 * name, when they differ.
 *
 * @example
 * const { fields, schemaNames } = useFields(() => "person")
 *
 * // Rows that wrap their record (a deal listing): schema fields read the
 * // record's data through `data`.
 * const { typeSchemaFields } = useFields(type, { data: (row) => row?.record?.data })
 */

import { computed, type MaybeRefOrGetter, toValue } from "vue"
import type { SchemaField } from "@auger/core"
import { enrichField, recordFixedFields } from "@auger/core/collections"
import { type AugerField, getListParentFields, getRelationshipFields } from "@auger/core/fields"
import { useSchemasStore } from "@/stores/schemas"
import { getType } from "@/stores/types"

/** What makes two schemas' definitions of one key the same field. */
function definition(f: SchemaField): string {
  const { type, label, values, recordType, subtype } = f as SchemaField & Record<string, unknown>
  return JSON.stringify([type, label ?? null, values ?? null, recordType ?? null, subtype ?? null])
}

export interface UseFieldsOptions {
  // The schemas the loaded records use, or null while none are loaded.
  schemaIds?: MaybeRefOrGetter<readonly number[] | null | undefined>
  // Where a row keeps the record's jsonb data; defaults to the row's own `data`.
  data?: (item: unknown) => Record<string, unknown> | null | undefined
}

export function useFields(
  recordTypeName: MaybeRefOrGetter<string | null | undefined>,
  options: UseFieldsOptions = {}
) {
  const schemasStore = useSchemasStore()

  const typeName = computed(() => toValue(recordTypeName) || null)
  const recordType = computed(() => (typeName.value ? getType(typeName.value) : null))
  // The schemas of this type (not the untyped ones).
  const schemas = computed(() => (typeName.value ? schemasStore.forType(typeName.value) : []))

  const schemaNames = computed(
    () => new Map(schemas.value.filter((s) => s.id && s.name).map((s) => [s.id, s.name] as const))
  )

  const { data } = options
  const schemaField = (f: SchemaField, schemaId: number | undefined) => {
    if (!data) return enrichField(f, { schema_id: schemaId, isEditable: true })
    const read = (item: unknown) => data(item)?.[f.key]
    return enrichField(f, { schema_id: schemaId, isEditable: true, value: read, rawValue: read })
  }

  // The record's own columns, enriched so reference fields (owner, schema, …)
  // resolve their ids.
  const fixedFields = computed(() =>
    recordFixedFields.map((f) => enrichField(f, { value: f.value }))
  )

  // Inherited from the record type.
  const typeSchemaFields = computed(() => {
    const schema = recordType.value?.schema
    return (schema?.fields ?? []).map((f) => schemaField(f, schema?.id))
  })

  // The other schemas of the type that the loaded records use, one field per
  // key where the schemas agree, else one per definition under the schema's name.
  const recordSchemaFields = computed(() => {
    const typeSchemaId = recordType.value?.schema?.id
    const ids = toValue(options.schemaIds)
    const byKey = new Map<
      string,
      Array<{ schema: (typeof schemas.value)[number]; f: SchemaField }>
    >()
    for (const schema of schemas.value) {
      if (schema.id === typeSchemaId || !Array.isArray(schema.fields)) continue
      if (ids && !ids.includes(schema.id)) continue
      for (const f of schema.fields) {
        const entries = byKey.get(f.key) ?? []
        entries.push({ schema, f })
        byKey.set(f.key, entries)
      }
    }

    const fields: AugerField[] = []
    for (const [key, entries] of byKey) {
      // Nothing loaded yet: every schema's field under its plain key, so a saved
      // column or filter keeps resolving until the records say which schemas count.
      if (!ids) {
        for (const { schema, f } of entries) fields.push(schemaField(f, schema.id))
        continue
      }
      const defs = new Map<string, typeof entries>()
      for (const entry of entries) {
        const sig = definition(entry.f)
        defs.set(sig, [...(defs.get(sig) ?? []), entry])
      }
      if (defs.size === 1) {
        const { schema, f } = entries[0]!
        fields.push(schemaField(f, schema.id))
        continue
      }
      for (const group of defs.values()) {
        const { schema, f } = group[0]!
        fields.push({
          ...schemaField(f, schema.id),
          key: `${schema.id}:${key}`,
          label: `${schema.name} · ${f.label ?? key}`,
        })
      }
    }
    return fields
  })

  // On-record relations: tags, contacts, times, attachments, sources, links.
  const relationshipFields = getRelationshipFields()

  // Events / Organizations / People: records joined through a list, computed
  // in Postgres (records_events, …). Only where the query selects them.
  const listRelationFields = getListParentFields()

  const fields = computed<AugerField[]>(() => [
    ...fixedFields.value,
    ...typeSchemaFields.value,
    ...recordSchemaFields.value,
    ...relationshipFields,
  ])

  return {
    recordType,
    schemas,
    schemaNames,
    fixedFields,
    typeSchemaFields,
    recordSchemaFields,
    relationshipFields,
    listRelationFields,
    fields,
  }
}
